import React from "react";
import { useTranslation } from 'react-i18next';
import ConnectionProgressStep from "./ConnectionProgressStep";

// "attention" is the third state the details-page banner needs: the step's thing
// exists but isn't configured yet (a destination attached with required fields
// still empty). The wizard only ever uses the first three.
export type StepStatus = 'completed' | 'in-progress' | 'upcoming' | 'attention';

type ConnectionProgressIndicatorProps = {
    stepZeroStatus: StepStatus;
    stepOneStatus: StepStatus;
    stepTwoStatus: StepStatus;
    webhookStepStatus?: StepStatus;
    confirmationStatus: StepStatus;
    setupSuccessful?: boolean;
    // How many accounts and flows still have required fields to fill in. Setup can
    // be structurally complete while the connection cannot move data yet.
    pendingSetupCount?: number;
}

const ConnectionProgressIndicator = ({
    stepZeroStatus,
    stepOneStatus,
    stepTwoStatus,
    webhookStepStatus,
    confirmationStatus,
    setupSuccessful = false,
    pendingSetupCount = 0,
}: ConnectionProgressIndicatorProps) => {
    const { t } = useTranslation();

    return (
        <div className="flex flex-col items-center py-2 bg-light-gray-button transition-colors duration-300">
            <div className="flex flex-col">
                <label className="mt-3 text-2xl text-off-black transition-colors duration-300">{t("Connection Quick Start")}</label>
                {setupSuccessful ?
                    <div className="my-2">
                        <ConnectionProgressStep
                            stepLabel={t("Success! Your connection is set up. Monitor the logs for status updates.")}
                            status="completed"
                            finalStep={true} />
                    </div> :
                    <>
                    <div className="flex w-max h-14 items-center gap-1 p-4 my-2 bg-white rounded-lg">
                        <ConnectionProgressStep stepLabel={t("Select Provider")} status={stepZeroStatus} finalStep={false} />
                        <ConnectionProgressStep stepLabel={t("Configure Provider")} status={stepOneStatus} finalStep={false} />
                        <ConnectionProgressStep stepLabel={t("Connect Destination")} status={stepTwoStatus} finalStep={false} />
                        {webhookStepStatus && (
                            <ConnectionProgressStep stepLabel={t("Configure Webhook")} status={webhookStepStatus} finalStep={false} />
                        )}
                        <ConnectionProgressStep stepLabel={t("Confirmation")} status={confirmationStatus} finalStep={true} />
                    </div>
                    {pendingSetupCount > 0 && (
                        <p className="text-sm text-off-black mb-3">
                            {t("Some required configurations are missing. Look for the exclamation marks on the flow map below.")}
                        </p>
                    )}
                    </>}
            </div>
        </div>
    );
};

export default ConnectionProgressIndicator